import { CART_SOURCE, type CartPartial } from './cart-partial.js';
import {
  normalizeCurrency,
  parseItemQuantity,
  shopifyCentsToValue,
} from './parse-money.js';

type ShopifyCartJson = {
  item_count?: unknown;
  total_price?: unknown;
  currency?: unknown;
  items?: unknown;
};

/** Normalize a Shopify `/cart.js` JSON body into a cart partial. */
export function normalizeShopifyCartJson(raw: unknown): CartPartial | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const cart = raw as ShopifyCartJson;

  const itemsCount = parseItemQuantity(cart.item_count);
  const cartValue = shopifyCentsToValue(cart.total_price);
  const currency = normalizeCurrency(cart.currency);

  if (itemsCount == null && cartValue == null && currency == null) return null;

  const emptyExplicit = itemsCount === 0;

  return {
    source: CART_SOURCE.shopify,
    confidence: 'high',
    ...(itemsCount != null ? { itemsCount } : {}),
    ...(cartValue != null ? { cartValue } : emptyExplicit ? { cartValue: 0 } : {}),
    ...(currency ? { currency } : {}),
    ...(emptyExplicit ? { emptyExplicit: true } : {}),
  };
}

export type FetchShopifyCartOptions = {
  /** Origin for `/cart.js` — defaults to `window.location.origin`. */
  origin?: string;
  fetchImpl?: typeof fetch;
};

/**
 * Fetch same-origin Shopify cart JSON. Returns null when not Shopify,
 * network fails, or body is unusable.
 */
export async function collectShopifyCartPartial(
  options: FetchShopifyCartOptions = {},
): Promise<CartPartial | null> {
  if (typeof window === 'undefined' && !options.origin) return null;

  const origin =
    options.origin ??
    (typeof window !== 'undefined' ? window.location.origin : undefined);
  if (!origin) return null;

  const fetchImpl = options.fetchImpl ?? fetch;
  try {
    const response = await fetchImpl(`${origin}/cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const json: unknown = await response.json();
    return normalizeShopifyCartJson(json);
  } catch {
    return null;
  }
}
